/* eslint-disable no-restricted-imports */
import * as React from 'react';
import { createRoot } from 'react-dom/client';

import { Badge, Heading, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';
import { renderWithUiRoot } from '@/js/wisemetering/ui/render_with_ui_root';

import storySnapshot from './stories.generated.json';

const STORY_SOURCES = storySnapshot.stories ?? {};

// Resolve a story render function back to the JSX source of its definition.
// Relies on the function's name matching its const declaration (which SWC
// preserves by default). The source is extracted at build time by
// scripts/generate-story-sources.js so what the catalog renders and what
// the catalog displays as code share a single literal in the source file.
const resolveStorySource = renderFn => STORY_SOURCES[renderFn?.name] ?? null;

const demoOptions = [
    { label: 'Alpha', value: 'alpha' },
    { label: 'Bravo', value: 'bravo' },
    { label: 'Charlie', value: 'charlie' },
    { label: 'Delta', value: 'delta' },
    { label: 'Echo', value: 'echo' }
];

const groupedOptions = [
    {
        heading: 'Core',
        options: demoOptions.slice(0, 3)
    },
    {
        heading: 'Extended',
        options: demoOptions.slice(3)
    }
];



const simpleTableRows = [
    { component: 'Button', family: 'Forms', status: 'Stable' },
    { component: 'Card', family: 'Layout', status: 'Stable' },
    { component: 'Select', family: 'Forms', status: 'Preview' },
    { component: 'Table', family: 'Display', status: 'Stable' },
    { component: 'Tabs', family: 'Navigation', status: 'Stable' },
    { component: 'Dialog', family: 'Overlays', status: 'Stable' },
    { component: 'Tooltip', family: 'Overlays', status: 'Preview' }
];

const createFamily = ({ api, description = '', id, label, layer = 'ui', stories = [], subcomponents = [] }) => ({
    api,
    description,
    id,
    label,
    layer,
    stories,
    subcomponents
});

const shikiTheme = 'dark-plus';
const highlightedCodeCache = new Map();

const normalizeShikiLanguage = language => {
    switch (language) {
        case 'js':
        case 'javascript':
        case 'jsx':
            return 'jsx';
        case 'json':
            return 'json';
        case 'bash':
        case 'sh':
            return 'bash';
        default:
            return 'jsx';
    }
};

const getHighlightedCode = async (code, language) => {
    const normalizedLanguage = normalizeShikiLanguage(language);
    const cacheKey = `${shikiTheme}:${normalizedLanguage}:${code}`;

    if (!highlightedCodeCache.has(cacheKey)) {
        highlightedCodeCache.set(cacheKey, import('shiki')
            .then(({ codeToHtml }) => codeToHtml(code, {
                lang: normalizedLanguage,
                theme: shikiTheme
            }))
        );
    }

    return highlightedCodeCache.get(cacheKey);
};

const StoryCard = ({ children, description, source, title }) => (
    <section className="tw-space-y-4 tw-border-b tw-border-border/70 tw-pb-8 last:tw-border-b-0 last:tw-pb-0">
        <div className="tw-space-y-1">
            <Heading as="h3" variant="title-sm">{title}</Heading>
            {description ? (
                <Text as="p" tone="muted" variant="body">{description}</Text>
            ) : null}
        </div>
        {children}
        {source ? (
            <div data-snapshot-omit>
                <CodeBlock code={source} language="jsx" />
            </div>
        ) : null}
    </section>
);

// Augment a family with `renderSection` on each of its stories — wires the
// story's render functions to a StoryCard plus the source code captured at
// build time. Family files use this instead of hand-rolling the .map().
//
// Each story can declare:
//   - render (optional): the "gallery" — shows all options/variants the
//     way the catalog has historically. Already provides its own surfaces
//     (PreviewSurface, VariantGallery, etc.) so it renders bare.
//   - hero   (optional): a single canonical example. Bare JSX of the
//     component; the catalog wraps it in PreviewSurface. Its source is
//     what `<CodeBlock>` displays.
//
// At least one of `render`/`hero` must exist. When both exist, the gallery
// is rendered above the hero and the hero's source is the displayed code.
// When only one exists, that one is rendered and supplies the code.
//
// We mount each as `<Component />` (not by calling it as a function) so
// any hooks live in their own component context — calling them inline
// pollutes the parent's hook list and triggers
// "Rendered fewer hooks than expected" when families with different hook
// counts are swapped.
const withStorySources = family => ({
    ...family,
    stories: family.stories.map(story => {
        const GalleryComponent = story.render ?? null;
        const HeroComponent = story.hero ?? null;
        const sourceFn = HeroComponent ?? GalleryComponent;

        return {
            ...story,
            renderSection: () => (
                <StoryCard
                    description={story.description}
                    source={resolveStorySource(sourceFn)}
                    title={story.title}
                >
                    {GalleryComponent ? <GalleryComponent /> : null}
                    {HeroComponent ? (
                        // When a gallery exists, the hero is auxiliary (it's
                        // there to provide the source for the CodeBlock). Hide
                        // it from snapshot tests so they keep matching the
                        // pre-hero baseline. When the hero is the ONLY render
                        // (no gallery), it IS the visual content — leave it
                        // visible to the snapshot tests.
                        <div {...(GalleryComponent ? { 'data-snapshot-omit': '' } : {})}>
                            <VariantPreview meta={GalleryComponent ? [{ label: 'preview', value: 'with code' }] : []}>
                                <HeroComponent />
                            </VariantPreview>
                        </div>
                    ) : null}
                </StoryCard>
            )
        };
    })
});

const CodeBlock = ({ code, language = 'jsx' }) => {
    const [highlightedHtml, setHighlightedHtml] = React.useState(null);

    React.useEffect(() => {
        let active = true;
        setHighlightedHtml(null);

        getHighlightedCode(code, language)
            .then(html => {
                if (active) {
                    setHighlightedHtml(html);
                }
            })
            .catch(() => {
                if (active) {
                    setHighlightedHtml(null);
                }
            });

        return () => {
            active = false;
        };
    }, [code, language]);

    return (
        <div className="tw-overflow-hidden tw-rounded-3xl tw-border tw-border-background/20 tw-bg-foreground">
            <div className="tw-border-b tw-border-background/20 tw-bg-foreground tw-px-4 tw-py-2 tw-text-muted-foreground tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide">
                {language}
            </div>

            {highlightedHtml ? (
                <div
                    className="code-block tw-overflow-hidden [&_.shiki]:tw-m-0 [&_.shiki]:tw-overflow-x-auto [&_.shiki]:tw-p-4 [&_.shiki]:tw-font-mono [&_.shiki]:tw-text-code [&_.shiki]:tw-leading-6 [&_.shiki_code]:tw-font-mono"
                    dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                />
            ) : (
                <pre className="tw-m-0 tw-overflow-x-auto tw-bg-foreground tw-p-4 tw-font-mono tw-text-code tw-leading-6 tw-text-background">
                    <code>{code}</code>
                </pre>
            )}
        </div>
    );
};

const ApiMetaPill = ({ tone = 'neutral', children }) => {
    const toneClassName = {
        default: 'tw-border-border tw-bg-muted tw-text-muted-foreground',
        neutral: 'tw-border-border tw-bg-muted tw-text-muted-foreground',
        required: 'tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700',
        optional: 'tw-border-border tw-bg-background tw-text-muted-foreground',
        type: 'tw-border-border tw-bg-background tw-text-foreground'
    }[tone] ?? 'tw-border-border tw-bg-muted tw-text-muted-foreground';

    return (
        <span className={['tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium', toneClassName].join(' ')}>
            {children}
        </span>
    );
};

// Convention: optional props/fields render their name with a trailing
// `?` (TypeScript-style), so a quick scan of a column of names tells
// the reader at-a-glance which entries are mandatory. The full
// "optional" / "required" word stays in the meta row to keep the
// declaration unambiguous for screen readers and copy-paste contexts.
const displayName = item => (item.required ? item.name : `${item.name}?`);

// Compact inner row for a single field within a prop's `fields` array.
// Same vocabulary as the top-level prop row (name | default | required
// | type, then description) but denser and indented under the parent.
const ApiFieldRow = ({ apiField }) => (
    <div className="tw-space-y-1.5 tw-rounded-xl tw-bg-muted tw-p-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
            <code className="tw-rounded-md tw-bg-background tw-px-2 tw-py-0.5 tw-text-code tw-font-semibold tw-text-foreground">
                {displayName(apiField)}
            </code>
            <Text tone="muted" variant="body-sm">|</Text>
            {apiField.defaultValue !== undefined ? (
                <>
                    <Text variant="body-sm">
                        default {apiField.defaultValue}
                    </Text>
                    <Text tone="muted" variant="body-sm">|</Text>
                </>
            ) : null}
            <Text variant="body-sm">
                {apiField.required ? 'required' : 'optional'}
            </Text>
            <Text tone="muted" variant="body-sm">|</Text>
            <code className="tw-text-code tw-font-medium tw-text-foreground">{apiField.type}</code>
        </div>
        <Text variant="body-sm">
            {apiField.description}
        </Text>
    </div>
);

const ApiPropRow = ({ apiProp }) => (
    <div className={`${WM_FLAT_SURFACE_CLASSNAME} tw-space-y-3 tw-rounded-2xl tw-p-4`}>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-2">
            <code className="tw-rounded-md tw-bg-muted tw-px-2 tw-py-1 tw-text-code tw-font-semibold tw-text-foreground">
                {displayName(apiProp)}
            </code>
            <Text tone="muted" variant="body">|</Text>
            {apiProp.defaultValue !== undefined ? (
                <>
                    <Text variant="body">
                        default {apiProp.defaultValue}
                    </Text>
                    <Text tone="muted" variant="body">|</Text>
                </>
            ) : null}
            <Text variant="body">
                {apiProp.required ? 'required' : 'optional'}
            </Text>
            <Text tone="muted" variant="body">|</Text>
            <code className="tw-text-code tw-font-medium tw-text-foreground">{apiProp.type}</code>
        </div>
        <Text variant="body">
            {apiProp.description}
        </Text>
        {apiProp.fields?.length ? (
            <div className="tw-space-y-2">
                {apiProp.fields.map(apiField => (
                    <ApiFieldRow key={`${apiProp.name}-${apiField.name}`} apiField={apiField} />
                ))}
            </div>
        ) : null}
    </div>
);

const ApiExportCard = ({ apiExport }) => {
    const isFunctionExport = /^[a-z]/.test(apiExport.name);
    const noun = isFunctionExport ? 'Parameters' : 'Props';

    return (
        <div className={`${WM_FLAT_SURFACE_CLASSNAME} tw-space-y-4 tw-rounded-3xl tw-p-5`}>
            <div className="tw-space-y-2">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <code className="tw-rounded-lg tw-bg-muted tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-foreground">
                        {apiExport.name}
                    </code>
                </div>
                <Text as="p" variant="body">
                    {apiExport.description}
                </Text>
            </div>

            {apiExport.props?.length ? (
                <div className="tw-space-y-3">
                    <Text tone="muted" variant="overline">
                        {noun}
                    </Text>
                    <div className="tw-grid tw-gap-3">
                        {apiExport.props.map(apiProp => (
                            <ApiPropRow key={`${apiExport.name}-${apiProp.name}`} apiProp={apiProp} />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-border tw-bg-muted tw-p-4 tw-text-muted-foreground tw-text-sm">
                    No component-specific {isFunctionExport ? 'parameters' : 'props'} are documented for this export.
                </div>
            )}

            {apiExport.notes?.length ? (
                <div className="tw-space-y-2">
                    <Text tone="muted" variant="overline">
                        Notes
                    </Text>
                    <div className="tw-space-y-2">
                        {apiExport.notes.map(note => (
                            <div
                                key={`${apiExport.name}-${note}`}
                                className="tw-rounded-2xl tw-border tw-border-border tw-bg-muted tw-p-4 tw-text-foreground tw-text-sm"
                            >
                                {note}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const PublicApiSection = ({ family }) => {
    if (!family.api?.exports?.length) {
        return null;
    }

    return (
        <section className="tw-space-y-4">
            <div className="tw-space-y-1">
                <Heading as="h3" variant="title-sm">Public API</Heading>
                <Text as="p" tone="muted" variant="body">
                    Public exports and supported props for this family.
                </Text>
                {family.api.summary ? (
                    <div className="tw-max-w-3xl">
                        <Text as="p" variant="body">
                            {family.api.summary}
                        </Text>
                    </div>
                ) : null}
            </div>

            <div className="tw-space-y-3">
                {family.api.exports.map(apiExport => (
                    <ApiExportCard key={apiExport.name} apiExport={apiExport} />
                ))}
            </div>
        </section>
    );
};

const preventPreviewNavigation = event => {
    // Submit events: cancel the default navigation as a safety net,
    // but let the form's own React onSubmit handler still fire. Using
    // stopPropagation here would intercept the event in the capture
    // phase and the form's listener would never run — that breaks
    // any demo whose Submit button is supposed to invoke real logic
    // (alert, fetch, console.log, etc).
    if (event.type === 'submit') {
        event.preventDefault();
        return;
    }

    // Click events: only swallow anchor navigation. Anything else
    // (radio toggles, button clicks, select dropdowns) must pass
    // through to its handler — historically this also matched any
    // `<form>` ancestor, which silently broke every interactive
    // control inside a demo form.
    const interactiveTarget = event.target instanceof HTMLElement
        ? event.target.closest('a[href]')
        : null;

    if (!interactiveTarget) {
        return;
    }

    // preventDefault stops the browser navigation, but Backbone has a
    // document-level jQuery delegate on `a[href^="/"]` that runs on
    // bubble and dispatches `Backbone.history.navigate` regardless of
    // the React handler. stopPropagation prevents the click from ever
    // reaching that delegate so clicks on demo links inside the
    // catalog stay confined to the preview surface.
    event.preventDefault();
    event.stopPropagation();
};

const PreviewSurface = ({ children, className = '', flush = false, padding = true }) => (
    <div
        className={[
            'tw-flex tw-min-h-catalog-preview-sm tw-w-full tw-flex-col tw-justify-center tw-gap-4',
            flush ? '' : 'tw-rounded-2xl tw-border tw-border-dashed tw-border-border tw-bg-muted',
            (flush || !padding) ? '' : 'tw-p-5',
            className
        ].filter(Boolean).join(' ').trim()}
        onClickCapture={preventPreviewNavigation}
        onSubmitCapture={preventPreviewNavigation}
    >
        {children}
    </div>
);

const ViewportPreviewSurface = ({
    children,
    className = '',
    frameClassName = ''
}) => {
    const mountRef = React.useRef(null);
    const rootRef = React.useRef(null);

    React.useEffect(() => {
        const mountNode = mountRef.current;

        if (!mountNode) {
            return undefined;
        }

        const root = createRoot(mountNode);
        rootRef.current = root;

        return () => {
            window.setTimeout(() => root.unmount(), 0);
            rootRef.current = null;
        };
    }, []);

    React.useEffect(() => {
        if (!rootRef.current || !mountRef.current) {
            return;
        }

        renderWithUiRoot(rootRef.current, mountRef.current, children);
    }, [children]);

    return (
        <PreviewSurface className={['tw-overflow-hidden tw-items-stretch', className].join(' ').trim()} padding={false}>
            <div
                ref={mountRef}
                className={[
                    'tw-relative tw-h-catalog-preview-lg tw-w-full tw-overflow-hidden tw-bg-transparent [transform:translateZ(0)]',
                    frameClassName
                ].join(' ').trim()}
            />
        </PreviewSurface>
    );
};

const PreviewStack = ({ children, className = '' }) => (
    <div className={['tw-flex tw-flex-col tw-gap-4', className].join(' ')}>
        {children}
    </div>
);

const PreviewRow = ({ children, className = '' }) => (
    <div className={['tw-flex tw-flex-wrap tw-items-center tw-gap-3', className].join(' ')}>
        {children}
    </div>
);

const PreviewGrid = ({ children, className = '' }) => (
    <div className={['tw-grid tw-gap-4 tw-grid-cols-1', className].join(' ')}>
        {children}
    </div>
);

// `columns` forces a fixed responsive grid (1 col mobile → 2 col md → N
// col lg) — use it when the matrix dimensions are meaningful and rows
// should align. Without `columns`, the grid auto-fits as many columns
// as fit at `minColumnWidth`.
const FIXED_COLUMN_CLASSNAMES = {
    2: 'tw-grid-cols-1 md:tw-grid-cols-2',
    3: 'tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3',
    4: 'tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4'
};

const LabeledPreviewGrid = ({ children, className = '', columns, minColumnWidth = '88px' }) => {
    const fixed = columns ? FIXED_COLUMN_CLASSNAMES[columns] : null;

    return (
        <div
            className={['tw-grid tw-w-full tw-gap-3', fixed, className].filter(Boolean).join(' ')}
            style={fixed ? undefined : { gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))` }}
        >
            {children}
        </div>
    );
};

const LabeledPreviewCell = ({
    children,
    className = '',
    label = null,
    labelClassName = '',
    minHeight = '96px',
    surfaceClassName = 'tw-bg-background'
}) => (
    <div
        className={[
            'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-border tw-p-4',
            surfaceClassName,
            className
        ].join(' ')}
        style={{ minHeight }}
    >
        {children}
        {label ? (
            <span className={['tw-text-foreground tw-text-xs tw-font-medium', labelClassName].join(' ').trim()}>
                {label}
            </span>
        ) : null}
    </div>
);

const VariantGallery = ({ children, className = '', columns = 3 }) => (
    <div className={[
        'tw-grid tw-grid-cols-1 tw-gap-4',
        columns === 2 ? 'md:tw-grid-cols-2 xl:tw-grid-cols-2' : 'md:tw-grid-cols-2 xl:tw-grid-cols-3',
        className
    ].join(' ')}>
        {children}
    </div>
);

const VariantMetaRow = ({ items = [] }) => (
    <div data-snapshot-hidden className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
        {items.map((item, index) => {
            const entry = typeof item === 'string'
                ? { label: item, value: null }
                : item;

            return (
                <span
                    key={`${entry.label}-${entry.value ?? index}`}
                    className="tw-shrink-0 tw-rounded-full tw-border tw-border-border tw-bg-muted tw-px-3 tw-py-1 tw-text-foreground tw-text-xs tw-font-medium"
                >
                    {entry.value === null || entry.value === undefined
                        ? entry.label
                        : `${entry.label}: ${entry.value}`}
                </span>
            );
        })}
    </div>
);

const VariantPreview = ({ children, className = '', flush = false, meta = [], padding = true }) => (
    <div className="tw-space-y-3">
        {meta.length ? <VariantMetaRow items={meta} /> : null}
        <PreviewSurface className={className} flush={flush} padding={padding}>
            {children}
        </PreviewSurface>
    </div>
);

const CompactVariantPreview = ({ className = '', ...props }) => (
    <VariantPreview
        {...props}
        className={['tw-min-h-catalog-preview-sm', className].join(' ').trim()}
    />
);

const SubcomponentList = ({ items }) => (
    <div className="tw-flex tw-flex-wrap tw-gap-2">
        {items.map(item => {
            const isString = typeof item === 'string';
            const name = isString ? item : item.name;
            const label = isString
                ? name
                : `${name} · ${item.inApp ?? 0} / ${item.inComponents ?? 0}`;

            return (
                <Badge key={name} label={label} tone="neutral" />
            );
        })}
    </div>
);

const FamilyHeader = ({ family }) => (
    <div className="tw-space-y-4">
        <div className="tw-space-y-2">
            <Text tone="muted" variant="overline">
                UI Catalog
            </Text>
            <Heading as="h2" variant="display">
                {family.label}
            </Heading>
            {family.description ? (
                <div className="tw-max-w-3xl">
                    <Text tone="muted" variant="body">
                        {family.description}
                    </Text>
                </div>
            ) : null}
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
            <MetaPill>{family.subcomponents.length} exports</MetaPill>
            {family.usage ? (
                <>
                    <MetaPill>{family.usage.inApp} in app</MetaPill>
                    <MetaPill>{family.usage.inComponents} in components</MetaPill>
                </>
            ) : null}
        </div>
        {family.subcomponents.length ? (
            <SubcomponentList items={family.usage?.perExport ?? family.subcomponents} />
        ) : null}
    </div>
);

const MetaPill = ({ children }) => (
    <span className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-border tw-bg-muted tw-px-3 tw-py-1 tw-text-muted-foreground tw-text-xs tw-font-medium">
        {children}
    </span>
);

export {
    ApiExportCard,
    CodeBlock,
    createFamily,
    demoOptions,
    FamilyHeader,
    groupedOptions,
    LabeledPreviewCell,
    LabeledPreviewGrid,
    MetaPill,
    PreviewGrid,
    PreviewRow,
    PreviewStack,
    PreviewSurface,
    PublicApiSection,
    ViewportPreviewSurface,
    VariantMetaRow,
    VariantGallery,
    VariantPreview,
    CompactVariantPreview,
    simpleTableRows,
    StoryCard,
    withStorySources
};
